import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { router } from '_/app/router';
import { AuthProvider } from '_/contexts/AuthContext';
import { SidebarProvider, TopHeaderProvider } from '_/contexts/LayoutContext';
import { SystemAlertProvider } from '_/contexts/SystemAlertContext';
import { endpoints } from '_/lib/frontend-utils';
import { notification } from 'antd';
import '_/utils/dateUtils';
import React from 'react';
import { createRoot } from 'react-dom/client';

// Configure global notification placement
notification.config({
  placement: 'bottomRight',
});

import './index.css';
import * as serviceWorker from './serviceWorker';
import { getPrettyName } from './utils/environmentUtils';

if (process.env.NODE_ENV === 'production') {
  const urlRegExp = new RegExp(`https://d3sgt82prjfpwv.cloudfront.net/zeus-ui/${process.env.BUILD_NUM}`, 'i');
  Sentry.init({
    dsn: 'https://cd9e84fa79b542a580d4a485db1b944f@o163897.ingest.sentry.io/6268579',
    release: process.env.BUILD_NUM,
    environment: getPrettyName(endpoints.rootDomain),
    integrations: [
      Sentry.rewriteFramesIntegration({
        iteratee: (frame) => {
          if (frame.filename && urlRegExp.test(frame.filename)) {
            frame.filename = frame.filename.replace(urlRegExp, '~/dist');
            frame.abs_path = frame.filename;
            frame.in_app = true;
          }
          return frame;
        },
      }),
    ],
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

const root = createRoot(rootElement);

root.render(
  <QueryClientProvider client={queryClient}>
    <SystemAlertProvider>
      <AuthProvider>
        <SidebarProvider>
          <TopHeaderProvider>
            <RouterProvider router={router} />
          </TopHeaderProvider>
        </SidebarProvider>
      </AuthProvider>
    </SystemAlertProvider>
  </QueryClientProvider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
